// src/pages/evcc/EvccMarketingToolkit.jsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";

const pageBg = "#f9fbfe";
const sectionHeaderBg = "#ccd87cff";
const sectionHeaderBorder = "#9fad3d";
const linkBlue = "#7e895d";

const getFileIcon = (type) => {
  const t = (type || "").toLowerCase();
  if (t === "pdf") return <PictureAsPdfIcon sx={{ fontSize: 18, color: "#fff" }} />;
  if (t === "png" || t === "jpg" || t === "jpeg" || t === "svg")
    return <ImageIcon sx={{ fontSize: 18, color: "#fff" }} />;
  return <DescriptionIcon sx={{ fontSize: 18, color: "#fff" }} />; // zip/other
};

const sections = [
  {
    title: "Logo",
    description:
      "By using the EVCC Logo in all your marketing and on your paperwork, you ensure that consumers see it and know that you are an EVCC member, serving to give consumers greater confidence.",
    items: [
      {
        label: "EVCC logo pack (zip)",
        type: "zip",
        href: "/assets/evcc/marketing/evcc-logo-pack.zip",
      },
      {
        label: "Guidance on using the EVCC logo (pdf)",
        type: "pdf",
        href: "/assets/evcc/marketing/evcc-logo-usage-guidance.pdf",
      },
    ],
  },
  {
    title: "Consumer Leaflets",
    description:
      "A handy consumer leaflet you can print and hand out to your customers. It explains what EVCC is and why choosing an EVCC member gives consumers peace of mind and confidence in their installation.",
    items: [
      {
        label: "EVCC Consumer Leaflet (pdf)",
        type: "pdf",
        href: "/assets/evcc/marketing/evcc-consumer-leaflet.pdf",
      },
    ],
  },
  {
    title: "Social Media content",
    description:
      "Use our ready-made social media templates and hashtags to spread the word about your EVCC membership. Let your customers know how your accreditation helps ensure safe, reliable, and high-quality EV charger installations.",
    items: [
      {
        label: "Social media templates (zip)",
        type: "zip",
        href: "/assets/evcc/marketing/evcc-social-templates.zip",
      },
      {
        label: "Suggested social media wording (pdf)",
        type: "pdf",
        href: "/assets/evcc/marketing/evcc-social-wording.pdf",
      },
    ],
  },
  {
    title: "Visuals for socials and email banners",
    description:
      "EVCC banners and graphics which can be easily embedded into your own website, email and social media channels.",
    items: [
      {
        label: "EVCC banner set (zip)",
        type: "zip",
        href: "/assets/evcc/marketing/evcc-banners.zip",
      },
    ],
  },
  {
    title: "VAN STICKERS",
    description:
      "As an EVCC member, you can get free van stickers to proudly display your accreditation on your vehicles to increase visibility while on the road.",
    items: [],
    extra: (
      <>
        <Typography variant="body2" sx={{ mt: 1, color: "#4b5563" }}>
          Just let us know how many you’d like and we can post these to you!
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: "#4b5563" }}>
          Drop us an email at{" "}
          <Box
            component="a"
            href="mailto:info@electric-vehicles.org.uk"
            sx={{
              color: linkBlue,
              fontWeight: 600,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            info@electric-vehicles.org.uk
          </Box>
          .
        </Typography>
      </>
    ),
  },
];

function SectionCard({ section }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #dde4f0",
        mb: 4,
      }}
    >
      <Box
        sx={{
          backgroundColor: sectionHeaderBg,
          borderTop: `4px solid ${sectionHeaderBorder}`,
          px: 3,
          py: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1d2736" }}>
          {section.title}
        </Typography>
      </Box>

      <Box sx={{ p: 3, backgroundColor: "#ffffff" }}>
        {section.description && (
          <Typography
            variant="body2"
            sx={{ mb: section.items.length ? 2 : 1, color: "#4b5563", maxWidth: 900 }}
          >
            {section.description}
          </Typography>
        )}

        {section.items.length > 0 && (
          <List disablePadding>
            {section.items.map((item, index) => (
              <React.Fragment key={item.label}>
                <ListItemButton
                  component="a"
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    alignItems: "flex-start",
                    px: 0,
                    "&:hover .linkText": { textDecoration: "underline" },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 48, mt: 0.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        backgroundColor: sectionHeaderBorder,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {getFileIcon(item.type)}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        className="linkText"
                        sx={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: linkBlue,
                        }}
                      >
                        {item.label}
                      </Typography>
                    }
                  />
                </ListItemButton>

                {index < section.items.length - 1 && (
                  <Divider sx={{ my: 1.75, borderColor: "#edf1f7" }} />
                )}
              </React.Fragment>
            ))}
          </List>
        )}

        {section.extra}
      </Box>
    </Paper>
  );
}

export default function EvccMarketingToolkit() {
  return (
    <Box sx={{ backgroundColor: pageBg, minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
          Home &gt; EVCC &gt; Marketing Toolkit
        </Typography>

        {/* Page title + intro */}
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 1, color: "#111827" }}
        >
          EVCC – Marketing Toolkit
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: "#4b5563", maxWidth: 900 }}>
          Use these EVCC marketing assets to promote your membership and show customers
          that you follow high standards for EV charger installations.
        </Typography>

        {sections.map((section) => (
          <SectionCard key={section.title} section={section} />
        ))}
      </Container>
    </Box>
  );
}
